import { useMemo } from 'react';

import { createSafeContext } from '@blockworks/ui/utils';

import { useDashboardGridContext } from '../components/dashboard-grid/context';
import { DashboardMode } from '../components/dashboard-grid/dashboard-grid.model';

import {
    DEFAULT_FEATURES_CONFIG,
    DEFAULT_LAYOUT_OPTIONS,
    HAS_ALL_FEATURES_CONFIG,
    type VisualizationFeatures,
    type VisualizationLayoutOptions,
} from './visualization-options.model';

type UseVisualizationOptionsStateProps = {
    /** Set of features to override visualizations */
    features?: VisualizationFeatures & { all?: boolean };
    layout?: VisualizationLayoutOptions;
};

const useVisualizationOptionsState = ({
    features: { all, dangerouslyGateDataExport, dangerouslyProxyRequests, ...restFeatures } = {},
    layout: layoutProp,
}: UseVisualizationOptionsStateProps) => {
    const { mode } = useDashboardGridContext();
    const isPreloadedMode = mode === DashboardMode.Preloaded;

    const features = useMemo(
        () => ({
            ...(all ? HAS_ALL_FEATURES_CONFIG : DEFAULT_FEATURES_CONFIG),
            ...restFeatures,
            dangerouslyGateDataExport: dangerouslyGateDataExport ?? isPreloadedMode,
            dangerouslyProxyRequests: dangerouslyProxyRequests ?? isPreloadedMode,
        }),
        [restFeatures, isPreloadedMode, all, dangerouslyGateDataExport, dangerouslyProxyRequests],
    );

    const layout = useMemo(
        () => ({
            ...DEFAULT_LAYOUT_OPTIONS,
            ...(layoutProp ?? {}),
        }),
        [layoutProp],
    );

    return {
        features,
        layout,
    };
};

type VisualizationOptionsContext = ReturnType<typeof useVisualizationOptionsState>;

const [VisualizationOptionsContextProvider, useVisualizationOptionsContext] =
    createSafeContext<VisualizationOptionsContext>({
        strict: false,
        name: 'VisualizationOptionsContext',
        hookName: 'useVisualizationOptionsContext',
        providerName: 'VisualizationOptionsContextProvider',
        defaultValue: {
            layout: {
                controlSize: 'xxs',
                defaultControl: 'toggle',
                hideLegend: false,
                headerHeight: 40,
            },
            features: {
                verboseErrorMessages: false,
                tableSearch: false,
                tablePagination: false,
                dangerouslyProxyRequests: false,
                dangerouslyGateDataExport: false,
            },
        },
    });

type VisualizationOptionsProviderProps = React.PropsWithChildren<UseVisualizationOptionsStateProps>;

/**
 * A context provider for visualization options and features.
 * e.g. if a user is signed in, turn the export feature on.
 *
 * This is currently (as of 10/22/24) implemented in the `DashboardGrid` so all visualizations in the dashboard can easily consume it.
 * This context was isolated so it can be reused in places where visualizations render that aren't in the `DashboardGrid`
 * e.g. studio's `VisualizationPreview`.
 */
const VisualizationOptionsProvider = ({ features, layout, children }: VisualizationOptionsProviderProps) => {
    const visualizationOptionsState = useVisualizationOptionsState({
        features,
        layout,
    });

    return (
        <VisualizationOptionsContextProvider value={visualizationOptionsState}>
            {children}
        </VisualizationOptionsContextProvider>
    );
};

export type { VisualizationOptionsContext, VisualizationOptionsProviderProps };
export { useVisualizationOptionsContext, VisualizationOptionsProvider };
